import { Stack, mergeStyleSets, Text, Icon } from "@fluentui/react";
import React, { useEffect, useState } from "react";
import { commonStyles } from "../CommonStyles/CommonStyles";
import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    AccordionToggleEventHandler,
    Link,
} from "@fluentui/react-components";
import { useSuccessStories } from "../../hooks/useHooks";
import "./SuccessStories.scss";
import { Spinner, SpinnerSize } from "@fluentui/react/lib/Spinner";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '../../utilities/AppInsights';
import appInsights, { generateCookieId } from "../../utilities/AppInsights";
import VideoPlayer from "../VideoPlayer/VideoPlayer";
import eventEmitter from "../../utilities/eventEmitter"; // Import the event emitter

const SuccessStories: React.FC = () => {
    const { data, isLoading, error } = useSuccessStories();
    const modifiedData = data?.map((item: any) => ({
        ...item,
        curatedPosition: item.curatedPosition.toString(),
    }));

    const images = (require as any).context("../../assets", true);
    const skillspeople = images("./people.png");

    const navigate = useNavigate();
    const { t } = useTranslation();
    const isRTL = document.body.classList.contains('langdirection-rtl');

    const styles = mergeStyleSets({
        storiesContainer: {
            backgroundColor: "#fff",
            padding: "50px 0px",
            "@media (max-width: 768px)": {
                marginTop: "0px",
            },
            "@media (max-width: 1024px)": {
                padding: "48px 0px",
            },
        },
        link: {
            color: "#0074D3 !important", textDecoration: "none", fontSize: "16px"
        },
        storiesHeading: {
            fontSize: "32px",
            fontWeight: 600,
            lineHeight: "40px",
            textAlign: "left"

        },
        storiesDescription: {
            fontSize: "16px",
            fontWeight: 400,
            lineHeight: "21.28px",
            textAlign: "left",
        },
        details: {
            backgroundColor: "white",
            padding: "30px 0px",
            borderRadius: "24px",
            "@media (max-width: 1024px)": {
                padding: "0px",
            },
        },
        storiesButton: {
            backgroundColor: "#0078D4",
            color: "white !important",
            borderRadius: "8px",
            marginTop: "20px",
            padding: "20px"
        },
        storiesImg: {
            animation: "slideIn 0.5s ease-in -out",
            borderRadius: "16px",
            "@media (max-width: 1400px)": {
                width: "500px !important",
            },
            "@media (max-width: 1024px)": {
                display: "none"
            },
            "@media (min-width: 1440px)": {
                width: "650px !important",
                height: "450px !important"
            },

            "@media (min-width: 1920px)": {
                width: "650px !important",
                height: "450px !important"
            },
        },
        storiesImgVideo: {
            animation: "slideIn 0.5s ease-in -out",
            borderRadius: "16px",
            zindex: "10",
            "@media (max-width: 1400px)": {
                width: "500px !important",
                height: "100%"
            },
            "@media (max-width: 1024px)": {
                display: "none"
            },
            "@media (min-width: 1440px)": {
                width: "650px !important",
                height: "450px !important"
            },

            "@media (min-width: 1920px)": {
                width: "650px !important",
                height: "450px !important"
            },
        },
        storiesImgVideoMob: {
            animation: "slideIn 0.5s ease-in -out",
            borderRadius: "16px",
            zindex: "10",
            "@media (max-width: 1400px)": {
                //  width: "500px !important",
                height: "100%"
            },
            "@media (min-width: 1025px)": {
                display: "none"
            },
            "@media (min-width: 768px) and (max-width: 1024px)": {
                height: "450px !important"
            },
        },

        storiesImgMob: {
            padding: "1rem 0rem",
            animation: "slideIn 0.5s ease-in -out",
            borderRadius: "16px",
            "@media (max-width: 1400px)": {
                width: "100% !important"
            },
            "@media (max-width: 1024px)": {
                display: "flex"
            },
            "@media (min-width: 1025px)": {
                display: "none"
            },
            "@media (min-height: 768px) and (max-height: 1024px)": {
                height: "450px !important"
            },
        },

    });


    const [currentImage, setCurrentImage] = useState<string | undefined>(undefined);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [imageChanged, setImageChanged] = useState<boolean>(false);
    const [openItems, setOpenItems] = useState<string[]>(["1"]);
    const [activeItem, setActiveItem] = useState<string | null>("1");
    const [playvideo, setplayvideo] = useState<boolean>(false);
    const [PositionId, SetPositionId] = useState<string>("1");

    // Function to check if URL is a video
    const isVideo = (url: string): boolean => {
        if (!url) return false;

        // Check for direct video file formats
        if (url.includes("videoplayer") || url.includes(".mp4") || url.includes(".webm") || url.includes(".avi")) {
            return true;
        }

        // Check if it's a YouTube video
        const youtubeRegex = /(youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/)/;
        if (youtubeRegex.test(url)) {
            return true;
        }

        return false;
    };


    // On Mount - Extract first media URL and set image/video
    useEffect(() => {
        if (!modifiedData || modifiedData.length === 0) return; // Ensure modifiedData exists

        const firstItem = modifiedData[0];
        const firstMediaUrl = firstItem.learnMoreUrl || "";

        let image = "people.png"; // Default image
        if (firstItem.imageUrl) {
            image = (firstItem.imageUrl.startsWith('http://') || firstItem.imageUrl.startsWith('https://')) ? firstItem.imageUrl : images(`./${firstItem.imageUrl}`);
        }

        let video = isVideo(firstMediaUrl) ? firstMediaUrl : null;

        // Ensure initial values are set if they are undefined/null
        if (currentImage === undefined) {
            setCurrentImage(image);
        }
        if (videoUrl === null) {
            setVideoUrl(video || "");
        }
    }, [modifiedData, currentImage, videoUrl, playvideo]);



    useEffect(() => {
        if (error) {
            navigate('/error');
        }
    }, [error, navigate]);

    useEffect(() => {
        if (imageChanged) {
            const timer = setTimeout(() => setImageChanged(false), 500);
            return () => clearTimeout(timer);
        }
    }, [imageChanged]);

    const handleToggle: AccordionToggleEventHandler<string> = (event, data) => {
        setOpenItems(data.openItems);
        setActiveItem(data.value);

        const selectedItem = modifiedData?.find((item: any) => item.curatedPosition === data.value);
        if (selectedItem) {
            SetPositionId(selectedItem.curatedPosition);
            const firstMediaUrl = selectedItem.learnMoreUrl || "";
            setCurrentImage((selectedItem.imageUrl.startsWith('http://') || selectedItem.imageUrl.startsWith('https://')) ? selectedItem.imageUrl : images(`./${selectedItem.imageUrl}`));

            if (isVideo(firstMediaUrl)) {
                setVideoUrl(firstMediaUrl);
            }
            else {
                setVideoUrl("");
            }

            setImageChanged(true);
        }
    };

    const heading = t('SuccessStories.heading', 'null')?.trim();
    const subHeading = t('SuccessStories.subHeading', 'null')?.trim();

    if (!heading || heading === "null" || !subHeading || subHeading === "null" || !data?.length) {
        return null;
    }

    return (
        <Stack className={styles.storiesContainer}>
            <Stack className={commonStyles.container}>
                <Stack horizontalAlign="center">
                    <Text variant="large" className={styles.storiesHeading}>
                        {t('SuccessStories.heading')}
                    </Text>
                    <Text as="h2" variant="medium" className={styles.storiesDescription} style={{ textAlign: "center" }}>
                        {t('SuccessStories.subHeading')}
                    </Text>
                </Stack>
                {isLoading && <Spinner size={SpinnerSize.large} />}
                {modifiedData?.length && !isLoading && (
                    <Stack horizontal className={styles.details}>
                        <Stack className={imageChanged ? "effect" : ""}>
                            {videoUrl ? (
                                <VideoPlayer videoUrl={videoUrl} thumbnail={currentImage} isPlayingvalue={playvideo} id={PositionId} ismoblescreen={false} className={styles.storiesImgVideo} section="SuccessStories" />
                            ) : (
                                <img src={currentImage} alt="success stories" className={styles.storiesImg} />
                            )}
                        </Stack>
                        <Stack className="detailsDiv">
                            <Accordion openItems={openItems} onToggle={handleToggle}>
                                {modifiedData?.map((item: any, index: number) => (
                                    <AccordionItem
                                        key={index}
                                        value={item.curatedPosition}
                                        className={`accordion-item ${activeItem === item.curatedPosition ? 'active' : ''}`}
                                    >
                                        <div className="item">
                                            <AccordionHeader expandIconPosition="end" className="storyHeading">
                                                {item.title}
                                            </AccordionHeader>
                                            <AccordionPanel className="storyDescription">
                                                <div style={{ paddingBottom: "10px" }}>{item.description}</div>
                                                <div className="link-div">
                                                    {videoUrl ? (
                                                        <Link
                                                            href=""
                                                            className="story-link"
                                                            onClick={() => {
                                                                const isMobileScreen = window.innerWidth <= 768;
                                                                eventEmitter.emit("playVideo", {
                                                                    videoUrl: item.learnMoreUrl,
                                                                    sourceComponent: "SuccessStories", // Add a unique identifier
                                                                    id: item.curatedPosition,
                                                                    isMobile: isMobileScreen,
                                                                });
                                                                trackEvent('SuccessStoriesLinkClick', {
                                                                    Title: item.title,
                                                                    ButtonText: item.buttonText,
                                                                    ExternalLink: item.learnMoreUrl,
                                                                    visitorId: generateCookieId(),
                                                                });
                                                            }}
                                                        >
                                                            {item.buttonText}
                                                            <Icon
                                                                iconName={isRTL ? "ChevronLeft" : "ChevronRight"}
                                                                className="story-link-icon"
                                                            />
                                                        </Link>
                                                    ) : (
                                                        <Link
                                                            href={item.learnMoreUrl}
                                                            target="_blank"
                                                            className="story-link"
                                                            onClick={() => {
                                                                trackEvent('SuccessStoriesLinkClick', {
                                                                    Title: item.title,
                                                                    ButtonText: item.buttonText,
                                                                    ExternalLink: item.learnMoreUrl,
                                                                    visitorId: generateCookieId(),
                                                                });
                                                            }}
                                                        >
                                                            {item.buttonText}
                                                            <Icon
                                                                iconName={isRTL ? "ChevronLeft" : "ChevronRight"}
                                                                className="story-link-icon"
                                                            />
                                                        </Link>
                                                    )}
                                                </div>
                                                <Stack className={imageChanged ? "effect" : ""}>
                                                    {videoUrl ? (
                                                        <VideoPlayer videoUrl={videoUrl} thumbnail={currentImage} isPlayingvalue={playvideo} ismoblescreen={true} id={PositionId} className={styles.storiesImgVideoMob} section="SuccessStories" />
                                                    ) : (
                                                        <img src={currentImage} alt="success stories" className={styles.storiesImgMob} />
                                                    )}
                                                </Stack>
                                            </AccordionPanel>
                                        </div>
                                    </AccordionItem>
                                ))}
                            </Accordion>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
};

export default SuccessStories;
